import { memo, useCallback, useEffect, useMemo, useRef, useState, type ElementType, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { cardAutoplayAllowed, useCardVideoAutoplay } from '../utils/cardVideoAutoplay'
import { buildTileVideoQueue, EMPTY_TILE_QUEUE, type TileVideoMember } from '../utils/tileVideoQueue'
import { useTileVideoQueue } from '../hooks/useTileVideoQueue'
import TileVideoWarm from './TileVideoWarm'
import { useNearViewport } from '../utils/nearViewport'
import { FlowLinkIcon } from '../icons/FlowLink.tsx'
import { Coin } from '../icons/Coin.tsx'
import PencilIcon from '../icons/PencilIcon'
import { createPortal } from 'react-dom'
import type { GenerationCard } from '../types/generationCard'
import { trendsLabel } from '../utils/pluralRu'
import CardModal from './CardModal'
import { Icon } from '../../public/icons/Icon.tsx'
import CardMediaBar from './CardMediaBar'
import { claimTileSound, onTileSoundClaimed, releaseTileSound, tileSoundToken } from '../utils/tileSound'
import DropletGlass from './DropletGlass'
import { CreateVideoIcon } from './CreateVideoIcon'
import { PillButton } from './ui/PillButton'
import CardImageLightbox, { type LightboxImage } from './CardImageLightbox'
import PreviewImg from './PreviewImg'
import SideTooltip from './SideTooltip'
import { getSourceImage } from '../utils/sourceImageDb'
import type { SunoTrack } from '../api/music'
import type { AudioOperation } from '../context/GenerateContext'
import { useSunoCoverIndex } from '../context/SunoCoverIndexContext'

// Возвращает URL первого аудио-трека карточки или fallback на previewUrl,
// если тот похож на audio-файл. null если это не audio.
function getAudioSrc(card: GenerationCard): string | null {
	if (card.kind !== 'audio') return null
	const first = card.audioTracks?.[0]
	if (first) return first
	if (card.previewUrl && /\.(mp3|wav|m4a|ogg|flac)(\?|$)/i.test(card.previewUrl)) {
		return card.previewUrl
	}
	return null
}

// Строит srcSet из previewUrlVariants для <img>. null если вариантов нет
// (старые записи до миграции — будет только src).
function buildPreviewSrcSet(card: GenerationCard): string | undefined {
	const v = card.previewUrlVariants
	if (!v) return undefined
	const parts: string[] = []
	if (v['320']) parts.push(`${v['320']} 320w`)
	if (v['640']) parts.push(`${v['640']} 640w`)
	if (v['1280']) parts.push(`${v['1280']} 1280w`)
	return parts.length ? parts.join(', ') : undefined
}

// sizes hint для браузера: на мобилке карточка = половина viewport,
// на десктопе — фикс ~260px.
const PREVIEW_SIZES = '(max-width: 768px) 50vw, 260px'

// Скрытый <audio> внутри audio-карточки галереи.
// Используется для hover-preview и resume-позиции при открытии CardModal.
const GenCardAudioMedia = memo(function GenCardAudioMedia({
	card,
	timesRef,
}: {
	card: GenerationCard
	timesRef: React.MutableRefObject<Map<string, number>>
}) {
	const audioRef = useRef<HTMLAudioElement>(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const audioSrc = getAudioSrc(card)

	useEffect(() => {
		const el = audioRef.current
		if (!el) return
		const onPlay = () => setIsPlaying(true)
		const onPause = () => setIsPlaying(false)
		const onTime = () => {
			if (!el.paused && el.currentTime > 0.05) {
				timesRef.current.set(card.id, el.currentTime)
			}
		}
		el.addEventListener('play', onPlay)
		el.addEventListener('pause', onPause)
		el.addEventListener('ended', onPause)
		el.addEventListener('timeupdate', onTime)
		return () => {
			el.removeEventListener('play', onPlay)
			el.removeEventListener('pause', onPause)
			el.removeEventListener('ended', onPause)
			el.removeEventListener('timeupdate', onTime)
		}
	}, [card.id, audioSrc, timesRef])

	return (
		<div
			className={`gen-card__media gen-card__media--audio${isPlaying ? ' gen-card__media--audio-playing' : ''}`}
		>
			<Icon id="gen-audio" size={32} />
			{card.audioTitle && (
				<div className="gen-card__media-audio-title">{card.audioTitle}</div>
			)}
			<div className="gen-card__audio-wave" aria-hidden>
				{Array.from({ length: 14 }).map((_, i) => (
					<span key={i} style={{ animationDelay: `${i * 0.08}s` }} />
				))}
			</div>
			{audioSrc && (
				<audio ref={audioRef} src={audioSrc} preload="metadata" />
			)}
		</div>
	)
})

// Загружает src видео только около viewport — и ВЫГРУЖАЕТ при уходе далеко
// (раньше src оставался навсегда: после прокрутки длинной видео-галереи сотни
// живых <video> с metadata копили память/декодеры — мобила задыхалась).
// Запас 600px в обе стороны: hover-play у края не обрывается от мелкого
// скролла, повторный вход берёт metadata из HTTP-кэша.
const LazyVideoMedia = memo(function LazyVideoMedia(
	{ src, className, poster, loopSrc, soundOn = false, onSoundLost, members, tileId, onPlayingMember }: {
		src: string
		className: string
		poster?: string
		/** Мульти-тренд: версии с роликами — плитка играет их по очереди (utils/tileVideoQueue). */
		members?: TileVideoMember[]
		tileId?: string
		/** Чей ролик очереди сейчас играет (подсветка кадра версии); null — очереди нет. */
		onPlayingMember?: (id: string | null) => void
		/** Лёгкий ролик карточки каталога: есть — плитка играет САМА (автоплей по
		 *  видимости, см. utils/cardVideoAutoplay). Нет (пользовательские
		 *  генерации — файлы любого веса) — прежнее «играем на наведении». */
		loopSrc?: string
		/** Звук включён баром: играем оригинал (src) без mute. */
		soundOn?: boolean
		/** Плитка перестала играть — звук гаснет (см. FeedVideo в ленте). */
		onSoundLost?: () => void
	},
) {
	// До play() видео не трогаем ВООБЩЕ, даже без постера: `preload=metadata`
	// тянет начало файла мегабайтами (замер: 5.3МБ из 6.3МБ — у mp4 с moov в
	// конце это почти весь файл), на телефоне лента видео «грузилась минутами»
	// (жалоба владельца 2026-07-31). Файл без постера — тёмная плитка с ▶ до
	// клика; постеры старым файлам даёт бэкфилл превью (новым — воркер).
	const preload = 'none'
	const ref = useRef<HTMLVideoElement>(null)
	// Обложку ставим, когда плитка рядом с экраном (utils/nearViewport): у <video> нет loading="lazy", и
	// poster длинной галереи качался бы сразу у всех плиток.
	const near = useNearViewport(ref, !!poster)
	const [active, setActive] = useState(false)
	const [managed] = useState(() => !!loopSrc && cardAutoplayAllowed())
	const { active: auto, setHovered } = useCardVideoAutoplay(ref, managed)
	const live = auto
	// Очередь роликов версий (23.10): состав — utils/tileVideoQueue, ход и прогрев следующего — hooks/useTileVideoQueue;
	// без версий очередь пуста и всё как раньше.
	const queue = useMemo(
		() => (members?.length ? buildTileVideoQueue({ id: tileId ?? '', src, loopSrc, poster }, members) : EMPTY_TILE_QUEUE),
		[members, tileId, src, loopSrc, poster],
	)
	const vq = useTileVideoQueue(ref, queue)
	useEffect(() => {
		onPlayingMember?.(vq.playingId)
	}, [vq.playingId]) // eslint-disable-line react-hooks/exhaustive-deps
	const cur = vq.queued ? vq.entry : null
	const origSrc = cur?.src ?? src
	const lightSrc = cur ? cur.loopSrc || cur.src : loopSrc
	// На смене ролика очереди — постер этой версии; первому ролику постер плитки, как раньше.
	const posterNow = cur && !vq.first ? cur.poster || poster : poster
	// Смена ролика (лёгкий ↔ оригинал) перезагружает элемент — запускаем заново
	// в обе стороны; замолкла не по кнопке — сообщаем наверх.
	useEffect(() => {
		if (live) void ref.current?.play().catch(() => {})
		else if (soundOn) onSoundLost?.()
	}, [soundOn, live, onSoundLost])
	// Наведение = смотреть с начала; ховер-эффекты плитки дают дребезг
	// enter/leave, поэтому перематываем не чаще раза в 600 мс.
	const lastRewind = useRef(0)
	const onEnter = () => {
		const v = ref.current
		const now = performance.now()
		if (v?.currentSrc && now - lastRewind.current > 600) {
			lastRewind.current = now
			v.currentTime = 0
		}
		setHovered(true)
	}

	useEffect(() => {
		const el = ref.current
		if (!el || managed) return
		const obs = new IntersectionObserver(
			([entry]) => setActive(entry.isIntersecting),
			{ rootMargin: '600px' },
		)
		obs.observe(el)
		return () => obs.disconnect()
	}, [managed])

	return (
		<>
		<video
			ref={ref}
			src={managed ? (live ? (soundOn ? origSrc : lightSrc) : undefined) : active ? origSrc : undefined}
			poster={near || live || active ? posterNow : undefined}
			className={`${className}${managed && live ? ' gen-card__media--live' : ''}`}
			data-autoplay={managed ? '1' : undefined}
			/* Наведение = смотреть с начала; уход курсора ролик не останавливает —
			   плитка остаётся в наборе автоплея и играет дальше. */
			onMouseEnter={managed ? onEnter : undefined}
			onMouseLeave={managed ? () => setHovered(false) : undefined}
			muted={!soundOn}
			// loop — паритет с ховер-превью ленты/витрины. Без него ролик
			// доигрывал и ЗАСТЫВАЛ на последнем кадре: постер после первого
			// play() не возвращается (спека), обложка выглядела «пропавшей».
			// Очередь версий: без loop, по концу ролика — следующий (useTileVideoQueue).
			loop={!vq.queued}
			playsInline
			preload={preload}
			onLoadedMetadata={e => { e.currentTarget.currentTime = 0 }}
			onEnded={vq.onEnded}
			onTimeUpdate={vq.onTimeUpdate}
			onPause={vq.onStop}
			onEmptied={vq.onStop}
		/>
		<TileVideoWarm entry={vq.warmEntry} soundOn={soundOn} />
		</>
	)
})

interface Props {
	items: GenerationCard[]
	title?: string
	className?: string
	/** masonry — «галерейная» раскладка (как Artlist): CSS columns, превью
	 *  с натуральными пропорциями, ноль JS — перф идентичен grid. */
	layout?: 'list' | 'grid' | 'masonry'
	/** Действие клика по карточке: 'modal' (default) — карточка-модал с
	 *  промптом; 'primary' — сразу onPrimaryAction (Тренды: промпт «под
	 *  капотом», клик подставляет инструмент в виджет без модала). */
	cardClickAction?: 'modal' | 'primary'
	onPrimaryAction?: (card: GenerationCard) => void
	/** Мульти-тренд (23.10): клик по кадру версии на плитке — открыть группу сразу с этой версией.
	 *  Без колбэка ряд декоративный. */
	onPickTrendMember?: (card: GenerationCard, memberId: string) => void
	onQuickAction?: (card: GenerationCard) => void  // быстрое действие без модала
	onCreateVideo?: (card: GenerationCard) => void
	onRemove?: (id: string) => void
	onMediaError?: (id: string) => void
	/** Колбэк для обновления списка — после Suno-действий (WAV/стемы/cover/…),
	 *  которые создают новые треки. Форвардится в CardModal → SunoActionsSheet. */
	onRefresh?: () => void
	/** Параметрические Suno-операции — форвардится в CardModal. Требует `trackLookup`
	 *  (чтобы получить полный SunoTrack из card.sunoTrackId). */
	onTrackParametricAction?: (track: SunoTrack, operation: AudioOperation) => void
	/** Маппинг id → SunoTrack. Нужен только когда передан `onTrackParametricAction`. */
	trackLookup?: (id: string) => SunoTrack | null
	/** Полный список треков — используется в Mashup picker чтобы не делать
	 *  лишний getUserTracks. Передаётся из BotsAudioLibrary. */
	tracksCache?: SunoTrack[]
	tourActive?: boolean   // первая карточка показывает кнопки принудительно
	onTourStart?: () => void // кнопка ? рядом с заголовком
	tourBtnSide?: 'left' | 'right' // сторона кнопки ? (default: left)
	/** Студия /generate: перекрёстная подсветка цепочки «фото → видео».
	 *  Карточки, чей previewUrl в списке, получают класс gen-card--linked.
	 *  Остальным галереям не передаётся — поведение прежнее. */
	linkedUrls?: string[]
	/** Ховер карточки (вход/выход) — студия связывает столы между собой. */
	onCardHover?: (card: GenerationCard, hovering: boolean) => void
	/** «Скачать» в ховер-оверлее каждой карточки. Передаёт только студия:
	 *  в галереях стилей/трендов скачивать нечего — там previewUrl витринный. */
	onDownload?: (card: GenerationCard) => void
	/** Свежая генерация: карточка с этим previewUrl получает gen-card--fresh
	 *  (кольцо + бейдж «НОВОЕ»). Отдельных действий у неё больше нет —
	 *  «Пересоздать» убрана, оценка живёт в модале карточки. */
	freshUrl?: string
	/** Первая ячейка сетки ПЕРЕД карточками — плитка-скелет активной
	 *  генерации в студии. С ним галерея рендерится и при пустом списке. */
	leadingTile?: ReactNode
	/** Ячейка ПОСЛЕ карточек — подсказка-соседка одинокого кадра в студии
	 *  («кликните, и получится версия»). Зеркало leadingTile. */
	trailingTile?: ReactNode
	/** Показывать действия карточек без ховера. Нужно ровно там, где юзер ещё
	 *  не знает, что карточка кликабельна (одинокий первый кадр в студии) —
	 *  подсказка рядом ссылается на кнопку, и та обязана быть видима. */
	revealActions?: boolean
	/** Персист оценки из CardModal (карточки истории с requestId). */
	onRated?: (card: GenerationCard, rating: number) => void
	/** Тач-режим студии: первый тап по карточке показывает её действия,
	 *  второй — выполняет основное (модал, а при cardClickAction='primary' —
	 *  само действие). Тренды tapReveal не передают — у них тап обязан сразу
	 *  применять инструмент. */
	tapReveal?: boolean
	/** «Глазик» полноразмерного просмотра обложки (витрины НЕ-инструментных
	 *  разделов: фотостудия, тренды). Мультистиль открывает лайтбокс с
	 *  пилюлями стилей. Студии/файлам не передаётся — там свои действия. */
	lightbox?: boolean
	/** Студия: действия карточки с ПОДПИСЯМИ («Изменить»/«Оживить»/«Ещё
	 *  вариант») + лупа для просмотра. Клик по такой карточке = primary
	 *  («в работу»), поэтому модал достижим только лупой (решение владельца).
	 *  Остальные галереи пропа не передают — их вид прежний. */
	actionLabels?: boolean
	/** Карточка «в работе» (её фото стоит в слоте бара) — кольцо-индикация,
	 *  связывающая плитку стола с баром. */
	workUrl?: string
	/** Студия: «Удалить» вариацию в меню шестерёнки — сносит ФАЙЛ с бэка (решение
	 *  владельца), потому с подтверждением вторым кликом. Другие галереи
	 *  пропа не передают — пункта нет. */
	onDelete?: (card: GenerationCard) => void
	/** Название карточки подписью на плитке (низ, поверх мягкого затемнения).
	 *  Витрины инструментов, где плитка — сам товар (Фотостудия): юзер должен
	 *  прочитать, что делает карточка, не открывая её. Галереи генераций пропа
	 *  не передают — там подпись только мешала бы. */
	showTitles?: boolean
}

export default function CardGallery({ items, title, className, layout = 'list', cardClickAction = 'modal', onPrimaryAction, onPickTrendMember, onQuickAction, onCreateVideo, onRemove, onMediaError, onRefresh, onTrackParametricAction, trackLookup, tracksCache, tourActive, onTourStart, tourBtnSide = 'left', linkedUrls, onCardHover, onDownload, freshUrl, leadingTile, trailingTile, revealActions, onRated, tapReveal, actionLabels, workUrl, onDelete, showTitles, lightbox }: Props) {
	// Мульти-тренд: чей ролик очереди сейчас на плитке (LazyVideoMedia сообщает) — кадр этой версии с ободком.
	const [playingMembers, setPlayingMembers] = useState<Record<string, string | null>>({})
	const setPlayingMember = useCallback((key: string, id: string | null) => {
		setPlayingMembers((prev) => (prev[key] === id ? prev : { ...prev, [key]: id }))
	}, [])
	// Лайтбокс «глазика»: набор картинок открытой карточки (null — закрыт).
	const [lightboxImages, setLightboxImages] = useState<LightboxImage[] | null>(null)
	// Звук на плитке (бар CardMediaBar): одна карточка в галерее, одна на страницу.
	const [soundCardId, setSoundCardId] = useState<string | null>(null)
	const [soundToken] = useState(tileSoundToken)
	useEffect(() => onTileSoundClaimed(soundToken, () => setSoundCardId(null)), [soundToken])
	const toggleSound = (id: string) => {
		if (soundCardId === id) {
			setSoundCardId(null)
			releaseTileSound()
		} else {
			setSoundCardId(id)
			claimTileSound(soundToken)
		}
	}
	const soundLost = useCallback((id: string) => setSoundCardId((cur) => (cur === id ? null : cur)), [])
	const openLightbox = (card: GenerationCard) => {
		const styles = card.card_kind === 'multistyle'
			? (card.demoVariants ?? []).filter((v) => !v.is_open && v.after_url)
			: []
		// Полноразмер по запросу: оригинал обложки (sourceImageUrl), превью —
		// только фолбэк; у стилей after_url и есть оригиналы.
		const cover = card.sourceImageUrl || card.previewUrl
		// Видео-карточка: смотреть надо ролик. Он лежит либо отдельным слоем над
		// обложкой (hoverVideoUrl), либо САМ является плиткой, когда обложки нет
		// (isVideo → previewUrl уже видео, постер — videoPosterUrl).
		const video = card.hoverVideoUrl || (card.isVideo ? card.previewUrl : undefined)
		const poster = card.isVideo ? card.videoPosterUrl || '' : cover
		setLightboxImages(
			styles.length
				? styles.map((v) => ({ url: v.after_url, label: v.label }))
				: video
					? [{ url: poster || '', label: card.title, video }]
					: cover
						? [{ url: cover, label: card.title }]
						: null,
		)
	}
	const [activeCard, setActiveCard] = useState<GenerationCard | null>(null)
	const [activeCardInitialTime, setActiveCardInitialTime] = useState(0)
	// Тач-режим (tapReveal): ховера нет, действия карточки иначе недостижимы —
	// первый тап их показывает, второй (по той же карточке) открывает модал.
	const [tappedId, setTappedId] = useState<string | null>(null)
	// Меню шестерёнки (студия, actionLabels): вторичные действия спрятаны в дропдаун
	// по образцу Artlist — открытые кнопки закрывали фото (решение владельца).
	// Рендерится ПОРТАЛОМ в body (fixed, якорь — right/bottom от кнопки):
	// внутри карточки его резал overflow:hidden обёртки кадра. Любой скролл
	// закрывает меню (иначе оно зависло бы в воздухе над уехавшей карточкой).
	const [menuId, setMenuId] = useState<string | null>(null)
	const [menuAnchor, setMenuAnchor] = useState<{ right: number; bottom: number } | null>(null)
	// «Удалить» в меню — двухшаговый: первый клик превращает пункт в «Точно
	// удалить?», второй удаляет. Любое закрытие меню сбрасывает подтверждение
	// (сброс — в самих обработчиках закрытия, не в эффекте: линтер против
	// setState в теле эффекта).
	const [confirmDelId, setConfirmDelId] = useState<string | null>(null)
	useEffect(() => {
		if (!menuId) return
		const close = () => {
			setMenuId(null)
			setConfirmDelId(null)
		}
		document.addEventListener('pointerdown', close)
		window.addEventListener('scroll', close, true)
		return () => {
			document.removeEventListener('pointerdown', close)
			window.removeEventListener('scroll', close, true)
		}
	}, [menuId])
	const isTouch = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches
	// Последние известные позиции inline-плейбека audio-карточек.
	// Используется для resume при открытии CardModal.
	const audioTimesRef = useRef<Map<string, number>>(new Map())

	// Резолвер обложек для audio-карточек без previewUrl (старые снапшоты,
	// сохранённые до того как бэк стал отдавать image_urls).
	const { getCover } = useSunoCoverIndex()

	/**
	 * Возвращает URL обложки для рендера: card.previewUrl если есть, иначе
	 * пробует резолвить через SunoCoverIndex по audioTracks[0]. Null-safe.
	 */
	function resolveAudioCover(card: GenerationCard): string {
		if (card.previewUrl) return card.previewUrl
		if (card.kind !== 'audio') return ''
		const firstAudio = card.audioTracks?.[0]
		return getCover(firstAudio) ?? ''
	}

	// При открытии карточки подгружаем исходник из IndexedDB
	const openCard = useCallback(async (card: GenerationCard) => {
		if (card.sourceImageDbKey && !card.sourceImageUrl) {
			try {
				const file = await getSourceImage(card.sourceImageDbKey)
				if (file) {
					const url = URL.createObjectURL(file)
					setActiveCard({ ...card, sourceImageUrl: url })
					return
				}
			} catch { /* fallback */ }
		}
		setActiveCard(card)
	}, [])

	// Плитка-скелет держит галерею живой и при пустом списке — первая
	// генерация в студии рисуется до появления первой карточки.
	if (items.length === 0 && !leadingTile) return null

	return (
		<>
			<div className={`card-gallery ${className || ''}`}>
				{title && (
					<div className="card-gallery__title">
						{onTourStart && tourBtnSide === 'left' && (
							<SideTooltip text="Как пользоваться" delay={0} preferSide="right">
								<button className="generate-tour-btn" onClick={onTourStart} type="button">?</button>
							</SideTooltip>
						)}
						{title}
						{onTourStart && tourBtnSide === 'right' && (
							<SideTooltip text="Как пользоваться" delay={0} preferSide="right">
								<button className="generate-tour-btn" onClick={onTourStart} type="button">?</button>
							</SideTooltip>
						)}
					</div>
				)}
				<div className={`card-gallery__list${layout === 'grid' ? ' card-gallery__list--grid' : ''}${layout === 'masonry' ? ' card-gallery__list--masonry' : ''}`}>
					{leadingTile}
					{items.map((card, idx) => {
						// Плитка с лендингом (card.href) — настоящая <a>: ссылку можно
						// скопировать/открыть в новой вкладке, краулер её видит. Клик
						// остаётся SPA-применением (preventDefault) — паритет с
						// PopularModelCard грид-страниц.
						const Tile = (card.href ? 'a' : 'div') as ElementType
						return (
						<Tile
							key={card.id}
							{...(card.href ? { href: card.href } : {})}
							data-card-slug={card.slug || undefined}
							className={`gen-card${card.card_kind === 'flow' ? ' gen-card--flow' : ''}${tourActive && idx === 0 ? ' gen-card--tour-active' : ''}${revealActions ? ' gen-card--show-actions' : ''}${card.tileStyle === 'droplet' ? ' glass-droplet-host' : ''}${linkedUrls?.includes(card.previewUrl) ? ' gen-card--linked' : ''}${freshUrl && card.previewUrl === freshUrl ? ' gen-card--fresh' : ''}${tappedId === card.id ? ' gen-card--tapped' : ''}${workUrl && card.previewUrl === workUrl ? ' gen-card--inwork' : ''}`}
							onClick={(e: ReactMouseEvent<HTMLElement>) => {
								if (card.href) e.preventDefault()
								// Тач: первый тап открывает действия карточки, второй — основное
								// действие (модал; в студии — «в работу»). Стоит РАНЬШЕ primary:
								// иначе тап в студии брал бы фото в работу вслепую, не показав
								// доступных действий. Тренды tapReveal не передают — их тап
								// по-прежнему применяет инструмент сразу. Тап по кнопке действия
								// сюда не долетает (stopPropagation).
								if (tapReveal && isTouch && card.kind !== 'audio' && tappedId !== card.id) {
									setTappedId(card.id)
									return
								}
								// Тренды и студия: клик = основное действие (применить инструмент /
								// взять фото в работу), модал открывает кнопка-лупа. Audio не
								// трогаем: у него resume-механика модала.
								if (cardClickAction === 'primary' && onPrimaryAction && card.kind !== 'audio') {
									onPrimaryAction(card)
									return
								}
								// Для audio — захватываем позицию inline-плейбека для resume
								// и ПРИНУДИТЕЛЬНО останавливаем inline-трек (на мобилке
								// mouseleave не фейрится → два трека играли бы одновременно).
								if (card.kind === 'audio') {
									const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
									const directTime = audio?.currentTime ?? 0
									const trackedTime = audioTimesRef.current.get(card.id) ?? 0
									setActiveCardInitialTime(Math.max(directTime, trackedTime))
									if (audio) {
										audio.pause()
										audio.currentTime = 0
									}
								} else {
									setActiveCardInitialTime(0)
								}
								openCard(card)
							}}
						>
							<div
								className="gen-card__media-wrap"
								onMouseEnter={(e) => {
									onCardHover?.(card, true)
									// Тач: браузер шлёт mouseenter ПЕРЕД тапом, а mouseleave не
									// шлёт вовсе — «ховер-превью» запускалось от тапа и играло
									// до конца, оставляя плитку на последнем кадре вместо
									// обложки (постер по спеке живёт до первого play()).
									// Без ховера превью не запускаем совсем.
									if (isTouch) return
									if (card.isVideo || card.hoverVideoUrl) {
										// Плитку под автоплеем не трогаем — она играет сама.
										const video = e.currentTarget.querySelector('video')
										if (video?.dataset.autoplay !== '1') video?.play().catch(() => {})
									} else if (card.kind === 'audio') {
										const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
										audio?.play().catch(() => {})
									}
								}}
								onMouseLeave={(e) => {
									onCardHover?.(card, false)
									if (card.isVideo || card.hoverVideoUrl) {
										const video = e.currentTarget.querySelector('video')
										if (video && video.dataset.autoplay !== '1') { video.pause(); video.currentTime = 0 }
									} else if (card.kind === 'audio') {
										const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
										if (audio) { audio.pause(); audio.currentTime = 0 }
									}
								}}
							>
								{card.kind === 'audio' ? (
									// Резолвим обложку: card.previewUrl → cover-index по audioUrl.
									// Показываем <img> только если реально есть URL картинки (не mp3).
									(() => {
										const coverUrl = resolveAudioCover(card)
										const isImage =
											coverUrl && !/\.(mp3|wav|m4a|ogg|flac)(\?|$)/i.test(coverUrl)
										if (isImage) {
											return (
												<img
													src={coverUrl}
													{...(card.previewUrlVariants && {
														srcSet: buildPreviewSrcSet(card),
														sizes: PREVIEW_SIZES,
													})}
													alt={card.audioTitle || card.prompt}
													className="gen-card__media"
													loading="lazy"
													decoding="async"
													width={260}
													height={195}
												/>
											)
										}
										return <GenCardAudioMedia card={card} timesRef={audioTimesRef} />
									})()
								) : card.isVideo ? (
									<LazyVideoMedia
										src={card.previewUrl}
										loopSrc={card.loopVideoUrl}
										poster={card.videoPosterUrl}
										className="gen-card__media"
										soundOn={soundCardId === card.id}
										onSoundLost={() => soundLost(card.id)}
										members={card.card_kind === 'multitrend' ? card.trendMembers : undefined}
										tileId={card.id}
										onPlayingMember={(id) => setPlayingMember(card.id, id)}
									/>
								) : card.previewUrl ? (
									<img
										src={card.previewUrl}
										// srcSet из backend'овских WebP thumbnail'ов (320/640/1280).
										// Только если миграция пройдена (есть previewUrlVariants) —
										// иначе src один, без srcSet/sizes.
										{...(card.previewUrlVariants && {
											srcSet: buildPreviewSrcSet(card),
											sizes: PREVIEW_SIZES,
										})}
										alt={card.title || card.prompt}
										className="gen-card__media"
										loading="lazy"
										decoding="async"
										width={260}
										height={195}
										onError={onMediaError ? () => onMediaError(card.id) : undefined}
									/>
								) : null /* ни превью, ни видео: слот держит фон media-wrap
									(пустой src="" давал ворнинги React и битую иконку) */}

								{/* Ховер-видео карточки ПОВЕРХ обложки — как её настроили в
								    админке («картинка + видео при наведении»). Одинаково во всех
								    местах, где живёт карточка: витрина, лента трендов, галерея —
								    раздел задаёт только форму плитки, не логику медиа. Прячем
								    прозрачностью, а не poster'ом: poster живёт лишь до первого
								    play(), после него обложка не вернулась бы никогда. */}
								{card.hoverVideoUrl && !card.isVideo && card.previewUrl && (
									<LazyVideoMedia
										src={card.hoverVideoUrl}
										loopSrc={card.loopVideoUrl}
										poster={card.previewUrl}
										className="gen-card__media gen-card__media--hover"
										members={card.card_kind === 'multitrend' ? card.trendMembers : undefined}
										tileId={card.id}
										onPlayingMember={(id) => setPlayingMember(card.id, id)}
									/>
								)}

								{/* Подпись-название витринной карточки (Фотостудия). Живёт ВНУТРИ
								    медиа-обёртки, а не строкой под ней: в masonry-колонках внешний
								    блок разрывал бы плитку между колонками. Кликам не мешает
								    (pointer-events на затемнении сняты), ховер-кнопки — выше. */}
								{showTitles && card.title && card.card_kind !== 'flow' && (
									<div className="gen-card__caption">
										{/* Мультистиль: свотчи-превью стилей НАД заголовком
										    (владелец 2026-08-16) — структура подписи галерейной
										    плитки не ломается, заголовок остаётся нижней строкой. */}
										{card.card_kind === 'multistyle' && (
											<span className="gen-card__msty-row" aria-hidden="true">
												{(card.demoVariants ?? [])
													.filter((v) => !v.is_open && v.after_url)
													.map((v) => (
														<PreviewImg key={v.key} className="gen-card__msty-swatch" src={v.after_url} alt="" loading="lazy" decoding="async" draggable={false} />
													))}
												{/* «Свой вариант» — карандаш последним (владелец 2026-08-21). */}
												{(card.demoVariants ?? [])
													.filter((v) => v.is_open)
													.map((v) => (
														v.after_url
															? <PreviewImg key={v.key} className="gen-card__msty-swatch" src={v.after_url} alt="" loading="lazy" decoding="async" draggable={false} />
															: <span key={v.key} className="gen-card__msty-swatch gen-card__msty-swatch--open" title={v.label}><PencilIcon size={12} /></span>
													))}
											</span>
										)}
										{/* Мульти-тренд (22.10): кадры участников над названием — тот же ряд, что у стилей. */}
										{card.card_kind === 'multitrend' && (card.trendMembers?.length ?? 0) > 0 && (onPickTrendMember ? (
											// Клик по кадру (23.10) — группа сразу с этой версией; к плитке событие не всплывает.
											<span className="gen-card__msty-row gen-card__msty-row--pick" role="group" aria-label="Тренды группы">
												{(card.trendMembers ?? []).map((m) => (
													<span
														key={m.id}
														role="button"
														tabIndex={0}
														className="gen-card__msty-swatch-btn"
														aria-label={`Открыть «${m.title}»`}
														title={m.title}
														onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPickTrendMember(card, m.id) }}
														onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onPickTrendMember(card, m.id) } }}
													>
														{/* Кадр версии, чей ролик сейчас на плитке, — с ободком (очередь роликов). */}
														{m.previewUrl
															? <PreviewImg className={`gen-card__msty-swatch${playingMembers[card.id] === m.id ? ' gen-card__msty-swatch--playing' : ''}`} src={m.previewUrl} alt="" loading="lazy" decoding="async" draggable={false} />
															: <span className={`gen-card__msty-swatch gen-card__msty-swatch--open${playingMembers[card.id] === m.id ? ' gen-card__msty-swatch--playing' : ''}`}>{m.title.slice(0, 2)}</span>}
													</span>
												))}
											</span>
										) : (
											<span className="gen-card__msty-row" aria-hidden="true">
												{(card.trendMembers ?? []).map((m) => (
													m.previewUrl
														? <PreviewImg key={m.id} className={`gen-card__msty-swatch${playingMembers[card.id] === m.id ? ' gen-card__msty-swatch--playing' : ''}`} src={m.previewUrl} alt="" loading="lazy" decoding="async" draggable={false} />
														: <span key={m.id} className={`gen-card__msty-swatch gen-card__msty-swatch--open${playingMembers[card.id] === m.id ? ' gen-card__msty-swatch--playing' : ''}`} title={m.title}>{m.title.slice(0, 2)}</span>
												))}
											</span>
										))}
										<span className="gen-card__caption-text">{card.title}</span>
									</div>
								)}

								{/* «Глазик» — полноразмерный просмотр обложки (только витрины
								    с lightbox-пропом: фотостудия/тренды; см. Props). */}
								{lightbox && card.previewUrl && (
									<CardMediaBar
										onExpand={() => openLightbox(card)}
										sound={card.loopVideoUrl ? { on: soundCardId === card.id, onToggle: () => toggleSound(card.id) } : undefined}
									/>
								)}

								{/* Карточка-флоу: чип «N шагов» — та же плашка, что «N стилей». */}
								{card.card_kind === 'flow' && typeof card.flowSteps === 'number' && card.flowSteps > 0 && (
									<span className="gen-card__msty" aria-label={`${card.flowSteps} шагов в мастерской`}>
										{card.flowSteps} {card.flowSteps === 1 ? 'шаг' : card.flowSteps < 5 ? 'шага' : 'шагов'}
									</span>
								)}
								{/* Мульти-тренд: бейдж «N трендов» — та же плашка, что «N стилей». */}
								{card.card_kind === 'multitrend' && (card.trendMembers?.length ?? 0) > 0 && (
									<span className="gen-card__msty" aria-label={`${trendsLabel(card.trendMembers!.length)} в группе`}>
										{trendsLabel(card.trendMembers!.length)}
									</span>
								)}
								{/* Мультистиль: бейдж «N стилей» (top-left, плотная плашка). */}
								{(() => {
									if (card.card_kind !== 'multistyle') return null
									const n = (card.demoVariants ?? []).filter((v) => !v.is_open && v.after_url).length
									if (n < 1) return null
									return (
										<span className="gen-card__msty" aria-label={`${n} стилей в карточке`}>
											{n} {n === 1 ? 'стиль' : n < 5 ? 'стиля' : 'стилей'}
										</span>
									)
								})()}

								{/* Акция стиля/тренда: «−N% · сколько осталось» (см. utils/discount;
								    поля посчитаны в StylesContext, у обычных генераций их нет). */}
								{card.discountPercent != null && (
									<span
										className="gen-card__discount"
										aria-label={`Скидка ${card.discountPercent}%`}
									>
										−{card.discountPercent}%{card.discountLeftLabel ? ` · ${card.discountLeftLabel}` : ''}
									</span>
								)}

								{(() => {
									const isFresh = !!freshUrl && card.previewUrl === freshUrl
									const showVideo = onCreateVideo && !card.isVideo && card.kind !== 'audio' && card.card_kind !== 'flow'
									const showQuick = onQuickAction && !isFresh
									const showDownload = onDownload && card.kind !== 'audio' && card.card_kind !== 'flow'
									// Студия (actionLabels): действия живут постоянным рядом ПОД
									// фото (gen-card__actionbar ниже по дереву), а ховер-оверлея
									// поверх медиа у неё нет вовсе — «Пересоздать» убрана, оценка
									// переехала в модал карточки (решение владельца 2026-08-01).
									if (actionLabels) return null
									if (!showVideo && !showQuick && !showDownload) return null
									return (
									<div className="gen-card__bottom-overlay">
										<div className="gen-card__actions">
											{(
												<>
													{showVideo && (
														<PillButton
															className="gen-card__create-video-btn"
															icon={<CreateVideoIcon size={20} />}
															tooltip="Создать видео из этой генерации"
															tooltipSide="left"
															onClick={(e) => { e.stopPropagation(); onCreateVideo(card) }}
														/>
													)}
													{showQuick && (
														<PillButton
															className="gen-card__quick-action-btn"
															icon={<Icon id="gen-reuse" size={20} />}
															tooltip="Применить стиль"
															tooltipSide="left"
															onClick={(e) => { e.stopPropagation(); onQuickAction(card) }}
														/>
													)}
													{showDownload && (
														<PillButton
															className="gen-card__download-btn"
															icon={<Icon id="gen-download" size={18} />}
															tooltip="Скачать"
															tooltipSide="left"
															onClick={(e) => { e.stopPropagation(); onDownload(card) }}
														/>
													)}
												</>
											)}
										</div>
									</div>
									)
								})()}

								{onRemove && (
									<button
										className="gen-card__remove"
										onClick={(e) => {
											e.stopPropagation()
											onRemove(card.id)
										}}
										type="button"
										title="Убрать"
									>
										<Icon id="gen-close" size={10} />
									</button>
								)}

							</div>
							{/* Студия: постоянный ряд действий ПОД фото (решение владельца
							    2026-08-01): «Изменить» с иконкой воссоздания + шестерёнка со
							    всем остальным (Оживить / Скачать / Удалить). Клик по самой
							    карточке открывает просмотр (cardClickAction 'modal'), поэтому
							    пункта «Посмотреть» в меню нет; «Ещё вариант» убран — его роль
							    целиком у «Изменить», и тот действует видимо. */}
							{actionLabels && (() => {
								const showEdit = !!onPrimaryAction && !card.isVideo && card.kind !== 'audio' && card.card_kind !== 'flow'
								const menuItems = [
									onCreateVideo && !card.isVideo && card.kind !== 'audio' && card.card_kind !== 'flow' &&
										{ key: 'video', label: 'Оживить', icon: <CreateVideoIcon size={16} />, run: () => onCreateVideo(card) },
									!!onDownload && card.kind !== 'audio' && card.card_kind !== 'flow' &&
										{ key: 'dl', label: 'Скачать', icon: <Icon id="gen-download" size={15} />, run: () => onDownload(card) },
									// Удаление вариации: двухшаговое подтверждение прямо в пункте
									// (меню не закрывается на первом клике).
									!!onDelete && {
										key: 'del',
										label: confirmDelId === card.id ? 'Точно удалить?' : 'Удалить',
										danger: true,
										keepOpen: confirmDelId !== card.id,
										icon: (
											<svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
												<path d="M3.5 5.5h13M8 5V3.8A1.3 1.3 0 0 1 9.3 2.5h1.4A1.3 1.3 0 0 1 12 3.8V5.5M5 5.5l0.7 10A1.6 1.6 0 0 0 7.3 17h5.4a1.6 1.6 0 0 0 1.6-1.5l0.7-10" />
											</svg>
										),
										run: () => {
											if (confirmDelId === card.id) onDelete(card)
											else setConfirmDelId(card.id)
										},
									},
								].filter(Boolean) as Array<{ key: string; label: string; icon: ReactNode; run: () => void; danger?: boolean; keepOpen?: boolean }>
								if (!showEdit && menuItems.length === 0) return null
								return (
									<div className="gen-card__actionbar" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
										{showEdit && (
											<PillButton
												className="gen-card__edit-btn"
												icon={<Icon id="gen-reuse" size={15} />}
												label="Изменить"
												tooltip="Взять фото в работу: опишите, что изменить"
												tooltipSide="left"
												onClick={(e) => { e.stopPropagation(); onPrimaryAction!(card) }}
											/>
										)}
										{menuItems.length > 0 && (
											<div className="gen-card__more-wrap">
												<PillButton
													className="gen-card__more-btn"
													icon={
														// Шестерёнка (канонический lucide-путь, не freehand).
														<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
															<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
															<circle cx="12" cy="12" r="3" />
														</svg>
													}
													tooltip={menuId === card.id ? undefined : 'Ещё действия'}
													tooltipSide="left"
													aria-label="Ещё действия"
													onClick={(e) => {
														e.stopPropagation()
														setConfirmDelId(null)
														if (menuId === card.id) { setMenuId(null); return }
														// Якорь right/bottom: меню растёт вверх-влево от кнопки,
														// его высоту знать не нужно.
														const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
														setMenuAnchor({
															right: window.innerWidth - r.right,
															bottom: window.innerHeight - r.top + 8,
														})
														setMenuId(card.id)
													}}
												/>
												{menuId === card.id && menuAnchor && createPortal(
													<div
														className="gen-card__menu"
														style={{ right: menuAnchor.right, bottom: menuAnchor.bottom }}
														onClick={(e) => e.stopPropagation()}
														onPointerDown={(e) => e.stopPropagation()}
													>
														{menuItems.map((it) => (
															<button
																key={it.key}
																type="button"
																className={`gen-card__menu-item${it.danger ? ' gen-card__menu-item--danger' : ''}`}
																onClick={(e) => {
																	e.stopPropagation()
																	if (!it.keepOpen) {
																		setMenuId(null)
																		setConfirmDelId(null)
																	}
																	it.run()
																}}
															>
																{it.icon}
																{it.label}
															</button>
														))}
													</div>,
													document.body,
												)}
											</div>
										)}
									</div>
								)
							})()}
							{/* «Стекло-капля» (tile_style=droplet): кромка+блик поверх медиа. */}
							{card.tileStyle === 'droplet' && <DropletGlass />}
							{/* Карточка из шаблона флоу — вариант A (владелец 05.09): обложка чистая,
							    под ней лента с иконкой «Флоу», названием и ценой «от». Живёт ВНЕ
							    медиа-обёртки — плитка выше на ленту, masonry это переживает. */}
							{card.card_kind === 'flow' && (
								<div className="gen-card__flow" aria-label={`Мастерская «${card.title || ''}»`}>
									<FlowLinkIcon size={18} className="gen-card__flow-icon" />
									<span className="gen-card__flow-title">{card.title}</span>
									{card.flowPriceFrom != null && (
										<span className="gen-card__flow-price" title="Все шаги по текущим настройкам шаблона">от {card.flowPriceFrom} <Coin width={12} height={12} /></span>
									)}
								</div>
							)}
						</Tile>
					)})}
					{trailingTile}
				</div>
			</div>

			{activeCard && (
				<CardModal
					card={activeCard}
					onRated={onRated}
					initialTime={activeCardInitialTime}
					onClose={() => {
						if (activeCard?.sourceImageUrl?.startsWith('blob:')) URL.revokeObjectURL(activeCard.sourceImageUrl)
						setActiveCard(null)
					}}
					onPrimaryAction={onPrimaryAction ? (card) => {
						onPrimaryAction(card)
						setActiveCard(null)
					} : undefined}
					onCreateVideo={onCreateVideo ? (card) => {
						onCreateVideo(card)
						setActiveCard(null)
					} : undefined}
					onRefresh={onRefresh}
					onTrackParametricAction={onTrackParametricAction}
					sourceTrack={activeCard.sunoTrackId && trackLookup ? trackLookup(activeCard.sunoTrackId) : null}
					tracksCache={tracksCache}
				/>
			)}

			{lightboxImages && (
				<CardImageLightbox images={lightboxImages} onClose={() => setLightboxImages(null)} />
			)}
		</>
	)
}
